'use client'

import { useQuery } from '@tanstack/react-query'

import { getSharedLayout } from '@/service/shared-layout.api'
import type { GetSharedLayoutResponse } from '@/types/generated/public_api'
import type { ErrorResponse } from '@/types/service'

// Not scoped by user: a share has no owner and reads the same for everyone,
// including logged-out visitors.
export const sharedLayoutKeys = {
  all: ['sharedLayout'] as const,
  detail: (shareId: string) => [...sharedLayoutKeys.all, shareId] as const,
}

/**
 * The client's error interceptor rejects with the raw `AxiosError` for
 * everything except the refresh path, which rejects with a flat
 * `{ status, message }`. Both shapes reach here, so read either.
 */
export function getErrorStatus(error: unknown): number | undefined {
  if (typeof error !== 'object' || error === null) return undefined
  const candidate = error as { status?: unknown; response?: { status?: unknown } }
  if (typeof candidate.response?.status === 'number') return candidate.response.status
  if (typeof candidate.status === 'number') return candidate.status
  return undefined
}

export const useGetSharedLayout = (shareId: string | null) => {
  return useQuery<GetSharedLayoutResponse, ErrorResponse>({
    queryKey: sharedLayoutKeys.detail(shareId ?? ''),
    queryFn: () => getSharedLayout(shareId!),
    enabled: !!shareId,
    // A share is immutable, so a successful read never needs refreshing.
    staleTime: Infinity,
    // A 404 will never become a 200 — anything else (a 500, a dropped
    // connection, a 429) is transient and must not be presented as a dead
    // link, so retry it rather than caching the failure for the tab's life.
    retry: (failureCount, error) => getErrorStatus(error) !== 404 && failureCount < 3,
  })
}
